import { baseApi, getLogger } from '@omnia/ui/src/lib'
import type { Film } from '@vista-digital/ocapi-types/v1'
import { getItemById, getOrderId, getOrderItems } from '@vista-digital/occ-domain'
import { MIN_SEARCH_LENGTH } from '@/common/const/minSearchLength.const'
import { endpoints } from '@/common/services/endpoints'
import type { FilmExtensionProps } from '@/features/film/hooks/useGetFilmExtension'
import type { ArticleDto } from '@/features/journal/components/article/journalArticle.dto'
import type { QueryArticlesParams } from '@/features/journal/types/queryArticlesParams'
import type { SearchResult } from '@/features/layout/header/subcomponents/searchButton/useSiteSearchResults'
import type {
    GetSubscriptionsResponse,
    SubscriptionsResponseDto,
} from '@/features/membership/hooks/useGetCmsSubscriptions'

class ApiService {
    private get logger() {
        return getLogger('ApiService').value
    }

    public queryArticles = async (qParams?: QueryArticlesParams) => {
        // eslint-disable-next-line prefer-const -- reassigning `tags` and `apiParams`
        let { signal = undefined, isPageLoaded, tags = [], loadOnScroll, ...apiParams } = qParams ?? {}
        tags = tags.filter(t => t !== 'All')
        apiParams = { ...apiParams, ...(tags.length > 0 ? { tags } : {}) } // to only include items that have values on the params

        const queryParams = new URLSearchParams(apiParams).toString()
        const url = endpoints.getJournals.replace('{params}', queryParams)
        const articles = await baseApi.GET<ArticleDto[]>(url, false, signal)
        return articles ?? []
    }

    public getHomeCinemaUrl = async (film: Film) => {
        try {
            const moviexchangeId = film.externalIds?.moviexchangeReleaseId
            if (!moviexchangeId) return

            const url = endpoints.getMoviexchangeId.replace('{moviexchangeId}', moviexchangeId)
            return await baseApi.GET<string>(url)
        } catch (error) {
            this.logger.error(`Failed to get home cinema url for film ${film.id}`, error)
            return
        }
    }

    public getSearchResults = async (searchTerm: string) => {
        let results = new Array<SearchResult>()
        try {
            if (searchTerm.length < MIN_SEARCH_LENGTH) return results

            const url = endpoints.getSearchResults.replace('{searchTerm}', searchTerm)
            results = (await baseApi.GET<SearchResult[]>(url)) ?? results
            return results
        } catch (error) {
            this.logger.error(`Failed to get search results for filter ${searchTerm}`)
            return results
        }
    }

    public getFilmExtension = async (filmId: string) => {
        try {
            const url = endpoints.getFilmExtensions.replace('{filmId}', filmId)
            return await baseApi.GET<FilmExtensionProps>(url)
        } catch (e) {
            this.logger.error('Failed to get film extension', e)
            return undefined
        }
    }

    public getSubscriptions: () => Promise<GetSubscriptionsResponse[] | undefined> = async () => {
        let results = new Array<GetSubscriptionsResponse>()
        try {
            const subscriptionData = (await baseApi.GET<SubscriptionsResponseDto[]>(endpoints.getSubscriptions)) ?? []
            results = subscriptionData?.map<GetSubscriptionsResponse>(sd => ({
                subscriptionTypes: sd.subscriptionType,
                nodeId: sd.nodeId,
                ...sd.subscription,
            }))
            return results
        } catch (e) {
            this.logger.error('Failed to get film extension', e)
            return undefined
        }
    }

    public saveBundleSelectionsToGiftShopOrder = async (siteId: string, signal?: AbortSignal) => {
        const selections = getOrderItems()

        const selectedItem = selections[0]
        const item = getItemById(selectedItem.itemId)

        if (!item) {
            throw new Error('Could not find the selected item')
        }

        return await baseApi.POST(
            endpoints.saveBundleSelectionsToGiftShopOrder,
            {
                orderId: getOrderId(),
                itemId: item.id,
                itemHoCode: item.hoCode,
                siteId: siteId,
                quantity: selections?.length,
            },
            true,
            signal
        )
    }
}

export const apiService = new ApiService()
